import { _axios } from '@/plugins/axios'
import qc from '@/plugins/converters/questionnaires'
import i18n from '@/plugins/i18n'

const state = {
  controls: [
    {
      id: 0,
      name: i18n.t('store.formBuilder.controls.title.name'),
      type: 'title',
      icon: 'mdi-format-title',
      divider: true,
      component: 'TitleInput'
    },
    {
      id: 1,
      name: i18n.t('store.formBuilder.controls.text.name'),
      type: 'text',
      icon: 'mdi-text-subject',
      divider: true,
      component: 'RichTextInput'
    },
    {
      id: 2,
      name: i18n.t('store.formBuilder.controls.questions.likert.name'),
      type: 'question',
      icon: 'mdi-lastpass',
      divider: true,
      component: 'LikertQuestionInput'
    },
    {
      id: 3,
      name: i18n.t('store.formBuilder.controls.questions.mcq.name'),
      type: 'question',
      icon: 'mdi-format-list-bulleted',
      divider: true,
      component: 'MCQQuestionInput'
    },
    {
      id: 4,
      name: i18n.t('store.formBuilder.controls.questions.open.name'),
      type: 'question',
      icon: 'mdi-text-short',
      divider: false,
      component: 'OpenQuestionInput'
    }
  ],
  /**
   * @typedef {Object} PageAir
   * @property {Number} id
   * @property {Number} pageNumber
   * @property {Number} questionnaireId
   * @property {Array} pageItems
   *
   * @typedef {PageAir[]}
   */
  workingPages: [],
  /**
   * @typedef {Object} QuestionnaireAir
   * @property {Number} id - Identifiant du questionnaire
   * @property {String} label - Label du questionnaire
   * @property {String} description - Description du questionnaire
   * @property {String} publicationDate - Date de publication éventuelle du questionnaire
   * @property {Boolean} isPublished - Indique si le questionnaire est publié
   * @property {PageAir[]} pages - Liste des pages du questionnaire
   * @property {Number} pageNumber - Nombre de page
   * @property {String} repository - Identifiant du référentiel associé
   * @type {QuestionnaireAir|null}
   */
  questionnaire: {},
  /**
   * @type {QuestionnaireAir[]}
   */
  questionnaires: []
}

const getters = {
  controls: state => state.controls,
  workingPages: state => state.workingPages,
  usedQuestionIds: state => state.workingPages.reduce(
    (questions, page) => questions.concat(page.pageItems.reduce(
      (questionIds, item) => questionIds.concat(
        item.type === 'question' ? [item.value] : []
      ), []
    )), []
  ),
  getPage: state => pageId => state.questionnaire.pages.find(
    page => page.id === pageId
  ),
  questionnaire: state => state.questionnaire,
  // eslint-disable-next-line no-unused-vars
  questionnaireSettings: state => (({ pages, pageNumber, ...settings }) => settings)(state.questionnaire),
  questionnaires: state => state.questionnaires
}

const actions = {
  itemsCUD: ({ commit, getters }, existingPages) => {
    const savePromises = []
    const deletePromises = []
    existingPages.forEach(
      page => {
        const oldPage = getters.getPage(page.id)
        page.pageItems.forEach(item => {
          if (item.id === null) {
            savePromises.push(
              _axios.post(
                `page-items/`,
                qc.JSToDJ.convertFormItem(item, page.id)
              ).then(response => {
                const savedItem = qc.DJToJS.convertFormItem(response.data)
                commit('ITEM_CREATE', { page: oldPage, pageItem: savedItem })
              })
            )
          } else {
            const oldItem = oldPage.pageItems.find(i => i.id === item.id)
            const { order, value, extra } = item

            let patchData = {}
            if (oldItem.order !== order) Object.assign(patchData, { order })
            if (oldItem.value !== value || oldItem.extra !== extra) {
              if (item.type === 'question') {
                Object.assign(patchData, { question: value })
              } else {
                Object.assign(patchData, { text: qc.JSToDJ.convertText(item) })
              }
            }

            if (Object.keys(patchData).length > 0) {
              savePromises.push(
                _axios.patch(`page-items/${item.id}/`, patchData)
                  .then(() => {
                    commit('ITEM_UPDATE', { page: oldPage, item })
                  })
              )
            }
          }
        })
        const pageItemsId = page.pageItems.map(item => item.id)
        const removedPageItemsId = oldPage.pageItems.map(item => item.id)
          .filter(id => !pageItemsId.includes(id))

        removedPageItemsId.forEach(itemId => {
          deletePromises.push(
            _axios.delete(`page-items/${itemId}/`)
              .then(() => {
                commit('ITEM_DELETE', { page: oldPage, itemId })
              })
          )
        })
      }
    )
    return Promise.all(savePromises.concat(deletePromises))
  },
  pagesCreation: ({ commit }, newPages) => {
    const postPromises = newPages.map(
      page => _axios.post('pages/', qc.JSToDJ.convertPage(page))
        .then(response => {
          commit('PAGE_CREATE', qc.DJToJS.convertPage(response.data))
        })
    )
    return Promise.all(postPromises)
  },
  pagesDeletion: ({ commit }, pagesId) => {
    let deletePromises = []
    pagesId.forEach(id => {
      deletePromises.push(
        _axios.delete(`pages/${id}/`)
          .then(() => {
            commit('PAGE_DELETE', id)
          })
      )
    })
    return Promise.all(deletePromises)
  },
  pagesNumberUpdate: ({ commit, getters }, existingPages) => {
    let updatePromises = []
    existingPages.forEach(
      page => {
        const newPageNumber = page.pageNumber
        const oldPage = getters.getPage(page.id)
        if (newPageNumber !== oldPage.pageNumber) {
          updatePromises.push(
            _axios.patch(
              `pages/${page.id}/set_number/`,
              { number: newPageNumber }
            ).then(() => {
              commit(
                'PAGE_NUMBER_UPDATE',
                { page: oldPage, pageNumber: newPageNumber }
              )
            })
          )
        }
      })
    return Promise.all(updatePromises)
  },
  questionnaireCopy: ({ commit, dispatch }, id) => {
    return _axios.get(`questionnaires/${id}/`)
      .then(response => {
        /** @type QuestionnaireDJ **/
        let questionnaire = response.data
        questionnaire.id = null
        questionnaire.label = `Copie de ${questionnaire.label}`
        questionnaire.publication_date = null
        questionnaire.is_published = false
        questionnaire.pages.forEach(p => {
          p.id = null
          p.questionnaire = null
          p.page_items.forEach(
            item => { item.id = null }
          )
        })
        commit('QUESTIONNAIRE_LOAD', questionnaire)
      })
      .catch(error => {
        dispatch('displayLoadError', error, { root: true })
      })
  },
  questionnaireDeletion: ({ commit, dispatch }, { id, label }) => {
    return _axios.delete(`questionnaires/${id}/`)
      .then(() => {
        commit('QUESTIONNAIRE_DELETE', id)
        dispatch('displayInfo',
          i18n.t('store.manager.questionnaire.deleted', { label }),
          { root: true })
      })
      .catch(error => {
        dispatch('displayError',
          { action: i18n.t('store.global.cannotDelete'), error },
          { root: true })
      })
  },
  questionnaireInit: ({ commit }, repositoryId) => {
    commit('QUESTIONNAIRE_INIT', repositoryId)
  },
  questionnaireLoad: ({ commit, state, dispatch }, id) => {
    // eslint-disable-next-line no-prototype-builtins
    if (!state.questionnaire.hasOwnProperty('id') || state.questionnaire.id !== id) {
      return _axios.get(`questionnaires/${id}/`)
        .then(response => {
          commit('QUESTIONNAIRE_LOAD', response.data)
        })
        .catch(error => {
          dispatch('displayLoadError', error, { root: true })
        })
    }
  },
  questionnaireLoadAll: ({ commit, dispatch }) => {
    return _axios.get('questionnaires/')
      .then(response => {
        commit('QUESTIONNAIRE_LOAD_ALL', response.data)
      })
      .catch(error => {
        dispatch('displayLoadError', error, { root: true })
      })
  },
  questionnaireSave: ({ commit, dispatch, state }, { questionnaire, isUpdate }) => {
    const data = qc.JSToDJ.convertQuestionnaireSettings(questionnaire)
    const { url, method, label } = isUpdate ? {
      url: `questionnaires/${questionnaire.id}/`,
      label: state.questionnaire.label,
      method: 'patch'
    } : {
      url: 'questionnaires/',
      label: questionnaire.label,
      method: 'post'
    }
    return (isUpdate
      ? _axios.patch(url, data)
      : _axios.post(url, data))
      .then(response => {
        commit('QUESTIONNAIRE_LOAD', response.data)
        dispatch('displayInfo',
          i18n.t(`store.manager.questionnaire.${method}.confirm`, { label }),
          { root: true })
      })
      .catch(error => {
        dispatch('displayError',
          { action: i18n.t(`store.manager.questionnaire.${method}.error`), error },
          { root: true })
      })
  },
  saveCopy: ({ commit, dispatch }, settings) => {
    return _axios.post('questionnaires/', qc.JSToDJ.convertQuestionnaireSettings(settings))
      .then(response => {
        commit(
          'QUESTIONNAIRE_UPDATE',
          qc.DJToJS.convertQuestionnaire(response.data, true)
        )
        return dispatch('saveWork', false)
      })
      .catch(error => {
        dispatch('displayError',
          { action: i18n.t(`store.manager.questionnaire.post.error`), error },
          { root: true })
      })
  },
  saveWork: ({ commit, state, dispatch }, notCopy) => {
    const { existingPages, newPages } = state.workingPages
      .reduce(
        (sortedPages, page) => {
          page.id
            ? sortedPages.existingPages.push(page)
            : sortedPages.newPages.push(page)
          return sortedPages
        }, { existingPages: [], newPages: [] }
      )
    const existingPagesId = existingPages.map(p => p.id)
    const removedPagesId = state.questionnaire.pages
      .reduce(
        (idList, page) => {
          if (page.id && !existingPagesId.includes(page.id)) idList.push(page.id)
          return idList
        }, []
      )
    dispatch('pagesDeletion', removedPagesId)
      .then(() => {
        dispatch('pagesNumberUpdate', existingPages)
          .then(() => {
            return Promise.all([
              dispatch('itemsCUD', existingPages),
              dispatch('pagesCreation', newPages)
            ])
              .then(() => {
                commit('WORKING_PAGES_REFRESH')
                const infoMode = notCopy ? 'save' : 'post'
                dispatch(
                  'displayInfo',
                  i18n.t(
                    `store.manager.questionnaire.${infoMode}.confirm`,
                    { label: state.questionnaire.label }),
                  { root: true })
              })
              .catch(error => {
                dispatch('displaySaveError',
                  { reason: i18n.t('store.manager.questionnaire.save.error.item'), error },
                  { root: true })
              })
          })
          .catch(error => {
            dispatch('displaySaveError',
              { reason: i18n.t('store.manager.questionnaire.save.error.pageOrder'), error },
              { root: true })
          })
      })
      .catch(error => {
        dispatch('displaySaveError',
          { reason: i18n.t('store.manager.questionnaire.save.error.deletePage'), error },
          { root: true })
      })
  },
  workingItemChange: ({ state, commit }, { pageIndex, itemIndex, field, value }) => {
    try {
      const item = state.workingPages[pageIndex].pageItems[itemIndex]
      commit('WORKING_ITEM_CHANGE', { item, field, value })
    } catch (e) {
      if (e instanceof TypeError) {
        throw Error(
          `no pageItem found in workingPages at pageIndex: ${pageIndex}, itemIndex: ${itemIndex}`
        )
      } else {
        console.log(e)
      }
    }
  },
  workingItemRemove: ({ commit }, { pageIndex, itemIndex }) => {
    try {
      const workingPage = state.workingPages[pageIndex]
      commit('WORKING_ITEM_REMOVE', { workingPage, itemIndex })
    } catch (e) {
      if (e instanceof TypeError) {
        throw Error(
          `no page found in workingPages at index: ${pageIndex}`
        )
      } else {
        console.log(e)
      }
    }
  },
  workingItemsUpdate: ({ commit }, { updatedItems, pageIndex }) => {
    const workingPage = state.workingPages[pageIndex]
    updatedItems.forEach((item, index) => { item.order = index + 1 })
    commit('WORKING_ITEMS_UPDATE', { updatedItems, workingPage })
  },
  workingPageInsert: ({ commit }, pageNumber) => {
    commit('WORKING_PAGE_INSERT', pageNumber)
  },
  workingPageRemove: ({ commit }, pageIndex) => {
    commit('WORKING_PAGE_REMOVE', pageIndex)
  },
  workingPageReset: ({ commit }, pageIndex) => {
    commit('WORKING_PAGE_RESET', pageIndex)
  },
  workingPagesRefresh: ({ commit }) => {
    commit('WORKING_PAGES_REFRESH')
  }
}

const mutations = {
  ITEM_CREATE: (state, { page, pageItem }) => {
    page.pageItems.push(pageItem)
  },
  ITEM_DELETE: (state, { page, itemId }) => {
    page.pageItems = page.pageItems.filter(item => itemId !== item.id)
  },
  ITEM_UPDATE: (state, { page, item }) => {
    const itemIndex = page.pageItems.findIndex(it => it.id === item.id)
    if (itemIndex < 0) {
      throw new Error(
        `item not found in questionnaire, page id: ${page.id}, item id ${item.id}`
      )
    } else {
      page.pageItems.splice(itemIndex, 1, item)
    }
  },
  PAGE_CREATE: (state, page) => {
    state.questionnaire.pages = [ ...state.questionnaire.pages, page ]
  },
  PAGE_DELETE: (state, pageId) => {
    state.questionnaire.pages = state.questionnaire.pages.filter(page => page.id !== pageId)
  },
  PAGE_NUMBER_UPDATE: (state, { page, pageNumber }) => {
    page.pageNumber = pageNumber
  },
  QUESTIONNAIRE_DELETE: (state, questionnaireId) => {
    state.questionnaire = {}
    state.questionnaires = state.questionnaires.filter(
      questionnaire => questionnaire.id !== questionnaireId
    )
  },
  QUESTIONNAIRE_INIT: (state, repositoryId) => {
    state.questionnaire = {
      id: null,
      label: '',
      description: '',
      publicationDate: null,
      publicationTime: null,
      isPublished: false,
      repository: repositoryId,
      pages: [ {
        id: null,
        pageNumber: 1,
        pageItems: []
      } ]
    }
  },
  QUESTIONNAIRE_LOAD: (state, questionnaire) => {
    state.questionnaire = qc.DJToJS.convertQuestionnaire(questionnaire, true)
    state.workingPages = [
      ...state.questionnaire.pages.map(
        p => ({ ...p, pageItems: p.pageItems.map(i => ({ ...i })) })) ]
  },
  QUESTIONNAIRE_LOAD_ALL: (state, questionnaires) => {
    state.questionnaires = questionnaires.map(
      questionnaire => qc.DJToJS.convertQuestionnaire(questionnaire, false)
    )
  },
  QUESTIONNAIRE_UPDATE: (state, questionnaire) => {
    state.questionnaire = questionnaire
    state.workingPages.forEach(page => {
      page.questionnaireId = questionnaire.id
    })
  },
  WORKING_ITEM_CHANGE: (state, { item, field, value }) => {
    if (field === 'titleType' || field === 'textLabel') {
      item.extra = value
    } else {
      item[field] = value
    }
  },
  WORKING_ITEM_REMOVE: (state, { workingPage, itemIndex }) => {
    workingPage.pageItems.splice(itemIndex, 1)
    workingPage.pageItems.forEach(
      (item, index) => { item.order = index + 1 }
    )
  },
  WORKING_ITEMS_UPDATE: (state, { workingPage, updatedItems }) => {
    workingPage.pageItems = [ ...updatedItems ]
  },
  WORKING_PAGE_INSERT: (state, pageIndex) => {
    const emptyPage = {
      id: null,
      pageItems: [],
      questionnaireId: state.questionnaire.id
    }
    state.workingPages.splice(pageIndex, 0, emptyPage)
    state.workingPages.forEach(
      (page, index) => { page.pageNumber = index + 1 }
    )
  },
  WORKING_PAGE_REMOVE: (state, index) => {
    state.workingPages.splice(index, 1)
    state.workingPages.forEach(
      (page, index) => { page.pageNumber = index + 1 }
    )
  },
  WORKING_PAGE_RESET: (state, index) => {
    state.workingPages[index].pageItems = []
  },
  WORKING_PAGES_REFRESH: state => {
    state.questionnaire.pages.sort(
      (page, otherPage) => page.pageNumber - otherPage.pageNumber)
    state.questionnaire.pages.forEach(page => {
      page.pageItems.sort(
        (item, otherItem) => item.order - otherItem.order)
    })
    state.workingPages = [
      ...state.questionnaire.pages.map(
        p => ({ ...p, pageItems: p.pageItems.map(i => ({ ...i })) })) ]
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
